
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton, Card, Avatar, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { ref, onValue, update, remove, query, limitToLast } from "firebase/database";
import useFirebase from 'hooks/useFirebase';
import { ChatThread } from 'types/chat';
import InlineChatWindow from 'components/chat/InlineChatWindow';
import FloatingChatWidget from 'components/chat/FloatingChatWidget';

// Audio URL for notification sound
const chatNotificationSound = "https://s3.eu-west-2.amazonaws.com/co.quikstep.dev/assets/ping.mp3";

// Styled components - compact notification design
const ChatNotificationCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
  background: '#fff',
  width: 280,
  position: 'relative',
  overflow: 'visible',
  cursor: 'pointer',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 24px rgba(0,0,0,0.2)',
  },
}));

const ChatAvatar = styled(Avatar)(({ theme }) => ({
  borderRadius: '10px',
  backgroundColor: theme.palette.primary.main,
  width: 36,
  height: 36,
  fontSize: '0.875rem',
  fontWeight: 600,
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: -6,
  right: -6,
  backgroundColor: '#fff',
  width: 20,
  height: 20,
  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  '&:hover': {
    backgroundColor: theme.palette.grey[100],
  },
  '& .MuiSvgIcon-root': {
    fontSize: '0.75rem',
  },
}));

const NotificationContainer = styled(Box)({
  position: 'fixed',
  top: 16,
  right: 16,
  zIndex: 1400,
  animation: 'slideIn 0.3s ease-out',
  '@keyframes slideIn': {
    from: {
      opacity: 0,
      transform: 'translateX(100%)',
    },
    to: {
      opacity: 1,
      transform: 'translateX(0)',
    },
  },
});

export interface ChatNotificationPayload {
  id?: string;
  type: 'CHAT_MESSAGE' | 'CHAT_THREAD_CREATED';
  threadId?: string;
  threadUid: string;
  transactionId: string;
  transactionKey: string;
  teamUid: string;
  messageId?: string;
  senderId: string;
  senderName: string;
  subject: string;
  message?: string;
  timestamp?: number;
  read?: boolean;
  mentionUids?: string[];
}

// Open chat window with transaction context
export interface OpenChatWindow {
  thread: ChatThread;
  transactionKey?: string;
}

interface ChatNotificationContextType {
  pendingChatNotification: ChatNotificationPayload | null;
  clearPendingChatNotification: () => void;
  triggerChatOpen: (threadUid: string) => void;
  notifications: ChatNotificationPayload[];
  unreadCount: number;
  markAsRead: (notificationId: string) => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  clearAllNotifications: () => Promise<void>;
  // Global chat window management
  openChatWindows: OpenChatWindow[];
  openChatWindow: (thread: ChatThread, transactionKey?: string) => void;
  closeChatWindow: (threadUid: string) => void;
}

const ChatNotificationContext = createContext<ChatNotificationContextType>({
  pendingChatNotification: null,
  clearPendingChatNotification: () => {},
  triggerChatOpen: () => {},
  notifications: [],
  unreadCount: 0,
  markAsRead: async () => {},
  deleteNotification: async () => {},
  clearAllNotifications: async () => {},
  // Global chat window management
  openChatWindows: [],
  openChatWindow: () => {},
  closeChatWindow: () => {},
});

export const useChatNotificationContext = () => useContext(ChatNotificationContext);

interface ChatNotificationProviderProps {
  children: React.ReactNode;
}

// Helper function to get initials from name
const getInitials = (fullName: string): string => {
  if (!fullName?.trim()) return "QS";
  const words = fullName.trim().split(/\s+/).slice(0, 2);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

// Helper function to truncate message
const truncateMessage = (message: string, maxLength: number = 60): string => {
  if (!message) return '';
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength) + '...';
};

export const ChatNotificationProvider: React.FC<ChatNotificationProviderProps> = ({ children }) => {
  const [notifications, setNotifications] = useState<ChatNotificationPayload[]>([]);
  const [visibleNotifications, setVisibleNotifications] = useState<ChatNotificationPayload[]>([]);
  const [pendingChatNotification, setPendingChatNotification] = useState<ChatNotificationPayload | null>(null);
  
  // Global chat windows state - persists across page navigation
  // Limit max open windows to prevent memory bloat
  const MAX_OPEN_WINDOWS = 5;
  const [openChatWindows, setOpenChatWindows] = useState<OpenChatWindow[]>([]);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { database, firebaseUid } = useFirebase();
  const lastNotificationTimestamp = useRef<number>(0);
  const isInitialLoad = useRef(true);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  // Limit processed IDs set size to prevent memory leak
  const MAX_PROCESSED_IDS = 100;
  const processedNotificationIds = useRef<Set<string>>(new Set());

  // Key the RTDB path on the AUTHENTICATED Firebase uid, not the backend user.uid.
  // The two are the same value for dashboard users, but firebaseUid is only set
  // once signInWithCustomToken has resolved — so gating on it makes every
  // chat-notifications listener wait for Firebase Auth instead of racing it and
  // getting denied by the owner-locked (auth.uid === $userUid) rule.
  const userUid = firebaseUid;

  // Initialize audio on mount
  useEffect(() => {
    console.log('[ChatNotif] initializing audio');
    audioRef.current = new Audio(chatNotificationSound);
    audioRef.current.preload = 'auto';
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
        console.log('[ChatNotif] audio cleaned up');
      }
    };
  }, []);

  // Play notification sound
  const playNotificationSound = useCallback(() => {
    try {
      const audio = audioRef.current;
      if (audio) {
        audio.currentTime = 0;
        audio.volume = 0.7;
        audio.play().catch((error) => {
          console.warn('Audio playback failed:', error);
        });
        console.log('[ChatNotif] played notification sound');
      } else {
        console.log('[ChatNotif] no audioRef to play');
      }
    } catch (error) {
      console.warn('Error playing notification sound:', error);
    }
  }, []);

  // Check URL for openChat parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const openChatThreadUid = searchParams.get('openChat');
    console.log('[ChatNotif] checking URL params for openChat', { openChatThreadUid });
    
    if (openChatThreadUid) {
      // Set pending notification to trigger chat opening
      const pending = {
        type: 'CHAT_MESSAGE',
        threadUid: openChatThreadUid,
        transactionId: '',
        transactionKey: '',
        teamUid: '',
        senderId: '',
        senderName: '',
        subject: ''
      } as ChatNotificationPayload;
      setPendingChatNotification(pending);
      console.log('[ChatNotif] set pendingChatNotification from URL', pending);
      
      // Remove the query parameter from URL without reload
      searchParams.delete('openChat');
      const newUrl = `${location.pathname}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
      window.history.replaceState({}, '', newUrl);
      console.log('[ChatNotif] removed openChat param from URL');
    }
  }, [location.pathname, location.search]);

  // Listen to Firebase Realtime Database for chat notifications
  useEffect(() => {
    console.log('[ChatNotif] useEffect - attach listener', { database: !!database, userUid });
    if (!database || !userUid) return;

    const notificationsRef = ref(database, `chat-notifications/${userUid}`);
    // Bound to the 100 most recent: the node accumulates (read notifications
    // aren't auto-deleted), so an unbounded onValue re-downloaded the entire
    // growing history on every notification write. The client only ever keeps
    // the newest 50, and new notifications are always the newest keys.
    const notificationsQuery = query(notificationsRef, limitToLast(100));

    const unsubscribe = onValue(notificationsQuery, (snapshot) => {
      console.log('[ChatNotif] onValue fired', { exists: snapshot.exists() });
      if (snapshot.exists()) {
        const data = snapshot.val();
        console.log('[ChatNotif] raw snapshot data', data);
        const notificationsList: ChatNotificationPayload[] = Object.entries(data).map(
          ([key, value]: [string, any]) => ({
            id: key,
            ...value,
          })
        );
        
        // Sort by timestamp descending (newest first)
        notificationsList.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
        console.log('[ChatNotif] sorted notificationsList', notificationsList.map(n => ({ id: n.id, timestamp: n.timestamp })));
        
        // Limit notifications stored in state to prevent memory bloat
        const MAX_STORED_NOTIFICATIONS = 50;
        const sliced = notificationsList.slice(0, MAX_STORED_NOTIFICATIONS);
        setNotifications(sliced);
        console.log('[ChatNotif] setNotifications', sliced.length);

        // Check for new notifications (not on initial load)
        if (!isInitialLoad.current && notificationsList.length > 0) {
          // Find notifications that haven't been processed yet
          const newNotifications = notificationsList.filter(n => 
            n.id && 
            !processedNotificationIds.current.has(n.id) &&
            n.timestamp && 
            n.timestamp > lastNotificationTimestamp.current
          );
          console.log('[ChatNotif] computed newNotifications', newNotifications.map(n => n.id));
          
          if (newNotifications.length > 0) {
            // Add each new notification to visible notifications
            newNotifications.forEach(notification => {
              if (notification.id) {
                processedNotificationIds.current.add(notification.id);
                // Limit processed IDs set size to prevent memory leak
                if (processedNotificationIds.current.size > MAX_PROCESSED_IDS) {
                  const idsArray = Array.from(processedNotificationIds.current);
                  processedNotificationIds.current = new Set(idsArray.slice(-MAX_PROCESSED_IDS));
                  console.log('[ChatNotif] trimmed processedNotificationIds', processedNotificationIds.current.size);
                }
              }
              setVisibleNotifications(prev => {
                // Check if notification already exists in visible list
                if (prev.some(n => n.id === notification.id)) {
                  console.log('[ChatNotif] visibleNotifications already has', notification.id);
                  return prev;
                }
                // Limit visible notifications to 5
                const newList = [notification, ...prev];
                const slicedVis = newList.slice(0, 5);
                console.log('[ChatNotif] adding to visibleNotifications', notification.id, 'new length', slicedVis.length);
                return slicedVis;
              });
            });
            
            // Play sound for new notifications
            playNotificationSound();
            
            // Update timestamp
            lastNotificationTimestamp.current = notificationsList[0].timestamp || Date.now();
            console.log('[ChatNotif] updated lastNotificationTimestamp', lastNotificationTimestamp.current);
          }
        } else if (isInitialLoad.current && notificationsList.length > 0) {
          // Set initial timestamp and mark existing as processed
          lastNotificationTimestamp.current = notificationsList[0].timestamp || Date.now();
          notificationsList.forEach(n => {
            if (n.id) {
              processedNotificationIds.current.add(n.id);
            }
          });
          console.log('[ChatNotif] initial load - marked existing processed ids', processedNotificationIds.current.size);
        }
        
        isInitialLoad.current = false;
      } else {
        setNotifications([]);
        isInitialLoad.current = false;
        console.log('[ChatNotif] snapshot empty - cleared notifications');
      }
    });

    console.log('[ChatNotif] listener attached');
    return () => {
      try {
        // Call the unsubscribe returned by onValue — the canonical modular-SDK
        // detach that works regardless of the query wrapper (the old
        // off(ref,'value',unsubscribe) passed the wrong callback and detached
        // nothing).
        unsubscribe();
        console.log('[ChatNotif] listener detached');
      } catch (err) {
        console.warn('[ChatNotif] error during listener detach', err);
      }
    };
  }, [database, userUid, playNotificationSound]);

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = useCallback(async (notificationId: string) => {
    console.log('[ChatNotif] markAsRead called', notificationId);
    if (!database || !userUid) {
      console.log('[ChatNotif] cannot mark as read - missing database or userUid', { database: !!database, userUid });
      return;
    }
    
    try {
      const notificationRef = ref(database, `chat-notifications/${userUid}/${notificationId}`);
      await update(notificationRef, { read: true });
      console.log('[ChatNotif] markAsRead success', notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }, [database, userUid]);

  const deleteNotification = useCallback(async (notificationId: string) => {
    console.log('[ChatNotif] deleteNotification called', notificationId);
    if (!database || !userUid) return;
    
    try {
      const notificationRef = ref(database, `chat-notifications/${userUid}/${notificationId}`);
      await remove(notificationRef);
      console.log('[ChatNotif] deleteNotification success', notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  }, [database, userUid]);

  const clearAllNotifications = useCallback(async () => {
    console.log('[ChatNotif] clearAllNotifications called');
    if (!database || !userUid) return;
    
    try {
      const notificationsRef = ref(database, `chat-notifications/${userUid}`);
      await remove(notificationsRef);
      console.log('[ChatNotif] clearAllNotifications success');
    } catch (error) {
      console.error('Error clearing all notifications:', error);
    }
  }, [database, userUid]);

  const handleNotificationClick = useCallback((notification: ChatNotificationPayload) => {
    console.log('[ChatNotif] handleNotificationClick', notification.id);
    const { teamUid, transactionId, transactionKey, threadUid, id } = notification;
    
    // Remove from visible notifications
    setVisibleNotifications(prev => prev.filter(n => n.id !== id));
    console.log('[ChatNotif] removed from visibleNotifications', id);
    
    // Mark as read and delete from Firebase
    if (id) {
      deleteNotification(id);
    }
    
    // Check if already on the transaction details page
    const targetPath = `/transactions/details/${teamUid}/${transactionId}/${transactionKey}`;
    
    if (location.pathname === targetPath) {
      // Already on the page, just set pending notification to open chat
      setPendingChatNotification(notification);
      console.log('[ChatNotif] set pendingChatNotification (already on page)', notification.id);
    } else if (teamUid && transactionId && transactionKey) {
      // Navigate to the transaction page with chat param
      navigate(`${targetPath}?openChat=${threadUid}`);
      console.log('[ChatNotif] navigating to transaction page with openChat', { targetPath, threadUid });
    }
  }, [location.pathname, navigate, deleteNotification]);

  const handleClose = useCallback((notification: ChatNotificationPayload) => {
    console.log('[ChatNotif] handleClose', notification.id);
    // Remove from visible notifications
    setVisibleNotifications(prev => prev.filter(n => n.id !== notification.id));
    
    // Also mark as read when closing
    if (notification.id) {
      markAsRead(notification.id);
    }
  }, [markAsRead]);

  const clearPendingChatNotification = useCallback(() => {
    console.log('[ChatNotif] clearPendingChatNotification called');
    setPendingChatNotification(null);
  }, []);

  const triggerChatOpen = useCallback((threadUid: string) => {
    console.log('[ChatNotif] triggerChatOpen', threadUid);
    setPendingChatNotification({
      type: 'CHAT_MESSAGE',
      threadUid: threadUid,
      transactionId: '',
      transactionKey: '',
      teamUid: '',
      senderId: '',
      senderName: '',
      subject: ''
    });
  }, []);

  // Global chat window management functions
  const openChatWindow = useCallback((thread: ChatThread, transactionKey?: string) => {
    console.log('[ChatNotif] openChatWindow called', thread.uid, transactionKey);
    setOpenChatWindows(prev => {
      // Check if window already exists
      if (prev.some(w => w.thread.uid === thread.uid)) {
        console.log('[ChatNotif] openChatWindow - already open', thread.uid);
        return prev;
      }
      // Limit max windows - close oldest if at limit
      const newWindows = prev.length >= MAX_OPEN_WINDOWS 
        ? prev.slice(1) // Remove oldest window
        : prev;
      const result = [...newWindows, { thread, transactionKey }];
      console.log('[ChatNotif] openChatWindows updated', result.map(r => r.thread.uid));
      return result;
    });
  }, []);

  const closeChatWindow = useCallback((threadUid: string) => {
    console.log('[ChatNotif] closeChatWindow called', threadUid);
    setOpenChatWindows(prev => {
      const result = prev.filter(w => w.thread.uid !== threadUid);
      console.log('[ChatNotif] openChatWindows after close', result.map(r => r.thread.uid));
      return result;
    });
  }, []);

  return (
    <ChatNotificationContext.Provider
      value={{
        pendingChatNotification,
        clearPendingChatNotification,
        triggerChatOpen,
        notifications,
        unreadCount,
        markAsRead,
        deleteNotification,
        clearAllNotifications,
        openChatWindows,
        openChatWindow,
        closeChatWindow,
      }}
    >
      {children}

      {/* Global Floating Chat Widget - Persists across all pages */}
      {userUid && (
        <FloatingChatWidget notifications={notifications} userUid={userUid} />
      )}

      {/* Global Chat Windows - Persist across page navigation */}
      {openChatWindows.map((chatWindow, index) => (
        <InlineChatWindow
          key={chatWindow.thread.uid}
          open={true}
          onClose={() => closeChatWindow(chatWindow.thread.uid)}
          thread={chatWindow.thread}
          transactionKey={chatWindow.transactionKey}
          windowIndex={index}
        />
      ))}
      
      {/* Multiple Chat Notifications UI - Stacked */}
      {visibleNotifications.length > 0 && (
        <NotificationContainer>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {visibleNotifications.map((notification, index) => (
              <ChatNotificationCard 
                key={notification.id || index}
                sx={{ 
                  p: 1.25,
                  animation: 'slideIn 0.3s ease-out',
                }}
                onClick={() => handleNotificationClick(notification)}
              >
                {/* Close button */}
                <CloseButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose(notification);
                  }}
                  aria-label="close notification"
                >
                  <CloseIcon />
                </CloseButton>
                
                {/* Notification content */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  {/* Avatar with initials */}
                  <ChatAvatar>
                    {getInitials(notification.senderName)}
                  </ChatAvatar>
                  
                  {/* Text content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* Sender name and transaction key */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25 }}>
                      <Typography 
                        variant="body2" 
                        fontWeight={600} 
                        sx={{ 
                          color: 'text.primary',
                          lineHeight: 1.2,
                        }}
                        noWrap
                      >
                        {notification.senderName || 'New Message'}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'primary.main',
                          fontWeight: 500,
                          fontSize: '0.65rem',
                        }}
                        noWrap
                      >
                        • {notification.transactionKey}
                      </Typography>
                    </Box>
                    
                    {/* Message preview */}
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary',
                        lineHeight: 1.3,
                        display: '-webkit-box',
                        WebkitLineClamp: 1,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {truncateMessage(notification.message || notification.subject || 'You have a new message', 40)}
                    </Typography>
                  </Box>
                </Box>
              </ChatNotificationCard>
            ))}
          </Box>
        </NotificationContainer>
      )}
    </ChatNotificationContext.Provider>
  );
};

export default ChatNotificationProvider;
